import { useEffect, useState } from 'react';
import { api } from '../services/api';
import {
  QUALIFYING_ENTRY_POLL_MS,
  buildQualifyingEntryActivity,
  buildQualifyingEntryActivityFromApi,
  qualifyingEntryQueryWindow,
  siteCalendarDay,
  type QualifyingEntryActivityView,
  type QualifyingEntryApiPayload,
} from '../domain/showcase/qualifyingEntryActivity';

const loadingView = (timeZone?: string | null): QualifyingEntryActivityView => (
  buildQualifyingEntryActivity({ status: 'loading', nowMs: Date.now(), timeZone })
);

export function useShowcaseQualifyingEntries(args: {
  enabled: boolean;
  siteId?: string | null;
  conditionId?: string | null;
  deviceId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  policyId?: string | null;
  timeZone?: string | null;
  selectedLocalDay?: string | null;
}): QualifyingEntryActivityView {
  const enabled = args.enabled;
  const siteId = String(args.siteId || '').trim();
  const conditionId = String(args.conditionId || '').trim();
  const deviceId = String(args.deviceId || '').trim();
  const cameraId = String(args.cameraId || '').trim();
  const zoneId = String(args.zoneId || '').trim();
  const policyId = String(args.policyId || '').trim();
  const timeZone = args.timeZone || null;
  const selectedLocalDay = String(args.selectedLocalDay || '').trim();
  const [view, setView] = useState<QualifyingEntryActivityView>(() => loadingView(timeZone));

  useEffect(() => {
    if (!enabled) return undefined;
    const ready = Boolean(siteId && conditionId && deviceId && cameraId && zoneId && policyId);
    if (!ready) {
      setView(loadingView(timeZone));
      return undefined;
    }
    let cancelled = false;
    let timer: number | undefined;

    const load = async () => {
      const nowMs = Date.now();
      try {
        const window = qualifyingEntryQueryWindow(nowMs, timeZone, selectedLocalDay || null);
        const params = new URLSearchParams({
          device_id: deviceId,
          camera_id: cameraId,
          zone_id: zoneId,
          policy_id: policyId,
          from: window.from,
          to: window.to,
        });
        const response = await api.get(
          `/api/v1/sites/${encodeURIComponent(siteId)}/conditions/${encodeURIComponent(conditionId)}/qualifying-entry-episodes?${params.toString()}`,
        );
        const data = (response?.data || {}) as QualifyingEntryApiPayload;
        if (cancelled) return;
        setView(buildQualifyingEntryActivityFromApi(data, {
          nowMs,
          timeZone,
          selectedLocalDay: selectedLocalDay || null,
        }));
      } catch {
        if (!cancelled) {
          setView(buildQualifyingEntryActivity({ status: 'error', nowMs: Date.now(), timeZone }));
        }
      }
    };

    const today = siteCalendarDay(new Date().toISOString(), timeZone);
    const closedHistorical = Boolean(selectedLocalDay && today && selectedLocalDay !== today);
    const loop = async () => {
      await load();
      if (cancelled || closedHistorical) return;
      timer = window.setTimeout(() => { void loop(); }, QUALIFYING_ENTRY_POLL_MS);
    };

    setView(loadingView(timeZone));
    void loop();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [cameraId, conditionId, deviceId, enabled, policyId, selectedLocalDay, siteId, timeZone, zoneId]);

  return view;
}
